import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Box,
    Button,
    Stack,
    Tabs,
    Tab,
    CircularProgress,
    Alert,
    Typography,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Paper,
    ButtonBase,
    Collapse,
    LinearProgress,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TerminalIcon from '@mui/icons-material/Terminal';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ApiService from '../network/API';
import {showFullscreenAd} from './ads/showFullscreenAd';
import AdSlot from './ads/AdSlot';
import LabTerminal, { isTouchDevice } from './LabTerminal';
import ProblemLimitsCard from './ProblemLimitsCard';
import {confirmDialog} from '../confirm';

// Реклама на экране терминала — там студент проводит почти всё время. Блоки обновляются раз в 90 с, пока видны на экране
// (правила РСЯ: не чаще раза в 30 с). Рядом с кнопками и клавишами рекламы нет: она не должна провоцировать случайные нажатия.
const LAB_AD_BLOCK_ID = 'R-A-20141312-4'; // адаптивный блок (боковая колонка на компьютере)
const TERMINAL_AD_BLOCK_ID = 'R-A-20141312-3'; // баннер (под терминалом на компьютере и под шапкой полноэкранного терминала на телефоне)
// Баннер внутри полноэкранного терминала на телефоне нужен ТОЛЬКО компактный (до ~100 px высотой). Адаптивный блок сайта отдавал
// вертикальные 300×600 и закрывал половину терминала, поэтому пока здесь null — реклама на телефоне идёт полноэкранной и нижним блоком.
// Когда в кабинете РСЯ создадут блок «320×100», его id нужно подставить сюда.
const MOBILE_TERMINAL_AD_BLOCK_ID = null;
const AD_REFRESH_SECONDS = 90;
// Полноэкранная реклама на экране терминала: раз в 5 минут, но только когда студент не печатает (≥15 с без ввода) — чтобы она
// не вылезала под пальцем посреди команды. Частоту дополнительно ограничивает сам блок в кабинете РСЯ.
const FULLSCREEN_AD_INTERVAL_MS = 5 * 60 * 1000;
const FULLSCREEN_AD_IDLE_MS = 15 * 1000;

const formatRemaining = (seconds) => {
    if (seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const fmt = (n) => String(Math.round(n * 100) / 100);

// Кнопка «Отправить на проверку»: состояние окружений оценивается по критериям задачи (как AI-проверка
// MySQL- и git-работ). Результат — баллы по критериям с комментариями; compact — результат в окне поверх терминала.
const LabCheck = ({ sessionId, problem, compact = false }) => {
    const [view, setView] = useState(null);
    const [busy, setBusy] = useState(false);
    const [outcome, setOutcome] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        ApiService.getMyCriteria(problem.id).then(setView).catch(() => setView(null));
    }, [problem.id]);

    const hasCriteria = view && (view.criteria.length > 0 || view.hiddenCount > 0);
    if (!hasCriteria) return null;

    const handleCheck = async () => {
        setBusy(true);
        setError(null);
        setOutcome(null);
        try {
            const result = await ApiService.checkLab(sessionId);
            setOutcome(result);
            setView(await ApiService.getMyCriteria(problem.id));
        } catch (err) {
            setError(err.reason || err.message || 'Не удалось отправить на проверку');
        } finally {
            setBusy(false);
            if (compact) setOpen(true);
        }
    };

    const status = outcome && ({
        OK: { severity: 'success', text: outcome.published ? 'Работа проверена, баллы записаны в оценку за лекцию.' : 'Работа проверена. Результат появится после публикации преподавателем.' },
        UNCHANGED: { severity: 'info', text: outcome.message || 'С прошлой проверки ничего не изменилось.' },
        SKIPPED: { severity: 'warning', text: outcome.message || 'Проверка пропущена.' },
        ERROR: { severity: 'error', text: outcome.message || 'Не удалось проверить работу, попробуйте позже.' },
    }[outcome.status] || { severity: 'info', text: outcome.message || outcome.status });

    const ratio = view && view.totalMaxPoints > 0 ? Math.min(1, view.totalEarnedPoints / view.totalMaxPoints) : 0;

    const details = (
        <Stack spacing={1.5}>
            {error && <Alert severity="error">{error}</Alert>}
            {status && <Alert severity={status.severity}>{status.text}</Alert>}
            {view && view.published && (
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography variant="subtitle1" fontWeight={700}>Результат</Typography>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {fmt(view.totalEarnedPoints)} <Typography component="span" color="text.secondary">из {fmt(view.totalMaxPoints)}</Typography>
                        </Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={ratio * 100} color={ratio >= 0.75 ? 'success' : ratio >= 0.4 ? 'warning' : 'error'}
                                    sx={{height: 8, borderRadius: 4, mt: 0.75, bgcolor: 'action.hover'}} />
                    <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{mt: 0.5, px: 0}}>
                        {showAll ? 'Скрыть критерии' : `Показать критерии (${view.criteria.length})`}
                    </Button>
                    <Collapse in={showAll} unmountOnExit>
                        <Stack divider={<Box sx={{borderBottom: 1, borderColor: 'divider'}} />} sx={{mt: 0.5}}>
                            {view.criteria.map((c) => {
                                const ok = c.earnedPoints != null && c.maxPoints && c.earnedPoints / c.maxPoints >= 0.999;
                                return (
                                    <Stack key={c.id} direction="row" spacing={1.25} alignItems="flex-start" sx={{py: 1}}>
                                        {ok
                                            ? <CheckCircleOutlineIcon color="success" fontSize="small" sx={{mt: '2px'}} />
                                            : <HighlightOffIcon color={c.earnedPoints > 0 ? 'warning' : 'error'} fontSize="small" sx={{mt: '2px'}} />}
                                        <Box sx={{minWidth: 0, flexGrow: 1}}>
                                            <Typography variant="body2" fontWeight={600} sx={{lineHeight: 1.35}}>{c.title}</Typography>
                                            {c.comment && !ok && <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.35, mt: 0.25}}>{c.comment}</Typography>}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap', lineHeight: 1.35}}>
                                            {fmt(c.earnedPoints ?? 0)} / {fmt(c.maxPoints)}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Collapse>
                </Box>
            )}
            {view && !view.published && !status && !error && (
                <Typography variant="body2" color="text.secondary">
                    Когда выполните задание, нажмите «Отправить на проверку» — оценка по критериям появится сразу.
                </Typography>
            )}
        </Stack>
    );

    return (
        <>
            <Button
                fullWidth={!compact}
                variant="contained"
                size="small"
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <FactCheckIcon />}
                onClick={handleCheck}
                disabled={busy}
            >
                {compact ? 'Проверить' : 'Отправить на проверку'}
            </Button>
            {compact ? (
                <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" sx={{ zIndex: 1600 }}>
                    <DialogTitle>Проверка работы</DialogTitle>
                    <DialogContent>{details}</DialogContent>
                    <DialogActions><Button onClick={() => setOpen(false)}>Закрыть</Button></DialogActions>
                </Dialog>
            ) : (outcome || error || (view && view.published)) && <Box sx={{ mt: 2 }}>{details}</Box>}
        </>
    );
};

const LabPanel = ({ problem }) => {
    const [session, setSession] = useState(null);
    const [stats, setStats] = useState([]);
    const [activeEnv, setActiveEnv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [now, setNow] = useState(Date.now());
    const [config, setConfig] = useState(null);
    const [fullscreen, setFullscreen] = useState(false);
    const [narrow, setNarrow] = useState(window.innerWidth < 900);

    const pollRef = useRef(null);
    const lastInputRef = useRef(Date.now());
    const lastFullscreenAdRef = useRef(Date.now());
    const noteActivity = useCallback(() => { lastInputRef.current = Date.now(); }, []);

    useEffect(() => {
        ApiService.getLabConfig().then(setConfig).catch(() => {});
    }, []);

    useEffect(() => {
        const onResize = () => setNarrow(window.innerWidth < 900);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const refresh = useCallback(async () => {
        try {
            const mine = await ApiService.getMyLab(problem.id);
            if (mine && mine.problemId === problem.id) {
                setSession(mine);
                // Через функциональное обновление, а не чтение activeEnv из замыкания —
                // иначе из-за устаревшего замыкания каждый опрос сбрасывал вкладку на первую.
                setActiveEnv((prev) => (prev && mine.environments.includes(prev)) ? prev : mine.environments[0]);
                if (mine.status === 'RUNNING') {
                    const s = await ApiService.getLabStats(mine.id);
                    setStats(s);
                }
            } else {
                setSession(mine); // окружение для другой задачи, если есть
            }
        } catch (err) {
            setError(err.reason || err.message || 'Не удалось получить статус окружения');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [problem.id]);

    useEffect(() => {
        refresh();
        pollRef.current = setInterval(refresh, 7000);
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => {
            clearInterval(pollRef.current);
            clearInterval(tick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [problem.id]);

    // Полноэкранная реклама по времени, пока окружение запущено и терминал виден.
    const sessionRunning = !!session && session.problemId === problem.id && session.status === 'RUNNING';
    const terminalVisible = sessionRunning && (!(isTouchDevice() && narrow) || fullscreen);
    useEffect(() => {
        if (!terminalVisible) return undefined;
        lastFullscreenAdRef.current = Math.max(lastFullscreenAdRef.current, Date.now() - FULLSCREEN_AD_INTERVAL_MS + 60 * 1000);
        const timer = setInterval(() => {
            const now = Date.now();
            if (document.visibilityState !== 'visible') return;
            if (now - lastFullscreenAdRef.current < FULLSCREEN_AD_INTERVAL_MS) return;
            if (now - lastInputRef.current < FULLSCREEN_AD_IDLE_MS) return;
            lastFullscreenAdRef.current = now;
            showFullscreenAd();
        }, 5000);
        return () => clearInterval(timer);
    }, [terminalVisible]);

    const handleStart = async () => {
        // Полноэкранная реклама на действии — частоту ограничивает сам блок в кабинете.
        showFullscreenAd();
        setStarting(true);
        setError(null);
        try {
            const created = await ApiService.startLab(problem.id);
            setSession(created);
            setActiveEnv(created.environments[0]);
        } catch (err) {
            setError(err.reason || err.message || 'Не удалось запустить окружение');
        } finally {
            setStarting(false);
        }
    };

    const handlePause = async () => {
        if (!session) return;
        setBusy(true);
        try {
            const updated = await ApiService.pauseLab(session.id);
            setSession(updated);
            setStats([]);
        } catch (err) {
            setError(err.reason || err.message || 'Не удалось приостановить окружение');
        } finally {
            setBusy(false);
        }
    };

    const handleResume = async () => {
        if (!session) return;
        showFullscreenAd(); // как и при запуске — естественный момент ожидания; частоту ограничивает сам блок
        setBusy(true);
        setError(null);
        try {
            const updated = await ApiService.resumeLab(session.id);
            setSession(updated);
        } catch (err) {
            setError(err.reason || err.message || 'Не удалось возобновить окружение');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!session) return;
        if (!await confirmDialog('Окружение будет удалено безвозвратно, вместе со всеми файлами внутри. Продолжить?')) return;
        setBusy(true);
        try {
            await ApiService.stopLab(session.id);
            setSession(null);
            setStats([]);
        } catch (err) {
            setError(err.reason || err.message || 'Не удалось удалить окружение');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    const isMine = session && session.problemId === problem.id;

    if (!isMine) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {session && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        У вас уже есть окружение для другой задачи («{session.problemTitle}»).
                        Завершите его, прежде чем запускать это.
                    </Alert>
                )}

                <Button
                    variant="contained"
                    startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                    onClick={handleStart}
                    disabled={starting || !!session}
                >
                    Запустить окружение
                </Button>
            </Box>
        );
    }

    if (session.status === 'PAUSED') {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <PauseCircleOutlineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>
                    Окружение приостановлено — все файлы внутри сохранены.
                </Typography>
                {session.pausedAt && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Если не возобновить, будет удалено безвозвратно{' '}
                        {new Date(new Date(session.pausedAt).getTime() + session.pausedRetentionDays * 86400000)
                            .toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </Typography>
                )}

                <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                        variant="contained"
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                        onClick={handleResume}
                        disabled={busy}
                    >
                        Возобновить
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteForeverIcon />}
                        onClick={handleDelete}
                        disabled={busy}
                    >
                        Удалить
                    </Button>
                </Stack>
            </Box>
        );
    }

    const idleDeadline = new Date(session.lastActivityAt).getTime() + session.idleTimeoutMinutes * 60000;
    const maxDeadline = new Date(session.startedAt).getTime() + session.maxSessionHours * 3600000;
    const deadline = Math.min(idleDeadline, maxDeadline);
    const remainingSeconds = Math.max(0, Math.floor((deadline - now) / 1000));
    const isLowTime = remainingSeconds < 120;

    const serverItems = [
        {
            icon: <AccessTimeIcon />,
            label: 'До автопаузы',
            value: formatRemaining(remainingSeconds),
            highlight: isLowTime,
        },
        ...stats.map((s) => ({
            icon: <SpeedIcon />,
            label: `${s.environmentName}: CPU / RAM`,
            value: `${s.cpuPercent?.toFixed(0) ?? 0}% / ${s.memUsageMb?.toFixed(0) ?? 0}–${s.memLimitMb?.toFixed(0) ?? 0} МБ`,
        })),
        ...stats.map((s) => ({
            icon: <StorageIcon />,
            label: `${s.environmentName}: диск`,
            value: config ? `${s.diskMb?.toFixed(0) ?? 0} / ${config.diskLimitMb} МБ` : `${s.diskMb?.toFixed(1) ?? 0} МБ`,
            highlight: config && s.diskMb >= config.diskLimitMb * 0.9,
        })),
    ];

    const mobileLayout = isTouchDevice() && narrow;

    // Все окружения держим подключёнными одновременно (скрытые не видны, но оболочка жива) —
    // переключение вкладки не убивает запущенные команды.
    const terminals = session.environments.map((env) => (
        <LabTerminal
            key={env}
            sessionId={session.id}
            env={env}
            mode={env !== activeEnv ? 'hidden' : (mobileLayout ? (fullscreen ? 'full' : 'hidden') : 'inline')}
            onClose={() => setFullscreen(false)}
            onActivity={noteActivity}
            ad={MOBILE_TERMINAL_AD_BLOCK_ID ? <AdSlot blockId={MOBILE_TERMINAL_AD_BLOCK_ID} refreshSeconds={AD_REFRESH_SECONDS} minHeight={64} sx={{ px: 1, py: 0.5, bgcolor: '#1e1e1e' }} /> : null}
            header={
                <>
                    {session.environments.length > 1 ? (
                        <Tabs
                            value={activeEnv}
                            onChange={(e, v) => setActiveEnv(v)}
                            variant="scrollable"
                            sx={{ minHeight: 32, flexGrow: 1, '& .MuiTab-root': { color: '#aaa', minHeight: 32, py: 0.5, textTransform: 'none' }, '& .Mui-selected': { color: '#7ee787 !important' } }}
                        >
                            {session.environments.map((e) => (
                                <Tab key={e} value={e} label={e} />
                            ))}
                        </Tabs>
                    ) : (
                        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>{env}</Typography>
                    )}
                    <LabCheck sessionId={session.id} problem={problem} compact />
                </>
            }
        />
    ));

    if (mobileLayout) {
        return (
            <Box sx={{ pb: 2 }}>
                {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', flexShrink: 0, bgcolor: isLowTime ? 'warning.main' : 'success.main',
                                   boxShadow: (t) => `0 0 0 4px ${isLowTime ? t.palette.warning.main : t.palette.success.main}33` }} />
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography fontWeight={700} sx={{ lineHeight: 1.3 }}>Окружение запущено</Typography>
                            <Typography variant="caption" color={isLowTime ? 'warning.main' : 'text.secondary'}>
                                Автопауза через {formatRemaining(remainingSeconds)}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack spacing={1} sx={{ mt: 2 }}>
                        {session.environments.map((env) => (
                            <ButtonBase
                                key={env}
                                onClick={() => { setActiveEnv(env); setFullscreen(true); }}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', textAlign: 'left', p: 1.25, borderRadius: 2.5,
                                    border: 1, borderColor: 'divider', bgcolor: 'action.hover',
                                    '&:active': { bgcolor: 'action.selected' },
                                }}
                            >
                                <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', flexShrink: 0,
                                           bgcolor: '#1e1e1e', color: '#7ee787' }}>
                                    <TerminalIcon />
                                </Box>
                                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, lineHeight: 1.3 }} noWrap>{env}</Typography>
                                    <Typography variant="caption" color="text.secondary">Открыть терминал</Typography>
                                </Box>
                                <ChevronRightIcon color="action" />
                            </ButtonBase>
                        ))}
                    </Stack>
                </Paper>

                <LabCheck sessionId={session.id} problem={problem} />

                <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                    <Button fullWidth size="small" color="inherit" variant="outlined" startIcon={busy ? <CircularProgress size={14} /> : <PauseIcon />}
                            onClick={handlePause} disabled={busy}>
                        Пауза
                    </Button>
                    <Button fullWidth size="small" color="error" variant="outlined" startIcon={<DeleteForeverIcon />} onClick={handleDelete} disabled={busy}>
                        Удалить
                    </Button>
                </Stack>
                {terminals}
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', gap: 2 }}>
            <Box sx={{ minWidth: 240, maxWidth: 280, display: { xs: 'none', md: 'block' }, overflowY: 'auto' }}>
                <ProblemLimitsCard title="Сервер" items={serverItems} />
                <Box sx={{ mt: 1.5 }}>
                    <LabCheck sessionId={session.id} problem={problem} />
                </Box>
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                        fullWidth
                        size="small"
                        startIcon={busy ? <CircularProgress size={16} /> : <PauseIcon />}
                        onClick={handlePause}
                        disabled={busy}
                    >
                        Приостановить
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        color="error"
                        startIcon={<DeleteForeverIcon />}
                        onClick={handleDelete}
                        disabled={busy}
                    >
                        Удалить
                    </Button>
                </Stack>
                <AdSlot blockId={LAB_AD_BLOCK_ID} refreshSeconds={AD_REFRESH_SECONDS} minHeight={250} sx={{ mt: 4 }} />
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, display: { xs: 'flex', md: 'none' } }}>
                    <Box sx={{ minWidth: 150 }}>
                        <LabCheck sessionId={session.id} problem={problem} compact />
                    </Box>
                    <Button size="small" startIcon={<StopIcon />} onClick={handlePause} sx={{ ml: 'auto' }}>
                        Пауза ({formatRemaining(remainingSeconds)})
                    </Button>
                    <Button size="small" color="error" startIcon={<DeleteForeverIcon />} onClick={handleDelete}>
                        Удалить
                    </Button>
                </Stack>

                {session.environments.length > 1 && (
                    <Tabs
                        value={activeEnv}
                        onChange={(e, v) => setActiveEnv(v)}
                        sx={{ minHeight: 32, mb: 1 }}
                    >
                        {session.environments.map((env) => (
                            <Tab key={env} value={env} label={env} sx={{ minHeight: 32, py: 0.5 }} />
                        ))}
                    </Tabs>
                )}

                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                    {terminals}
                </Box>
                <AdSlot blockId={TERMINAL_AD_BLOCK_ID} refreshSeconds={AD_REFRESH_SECONDS} minHeight={72} sx={{ mt: 1 }} />
            </Box>
        </Box>
    );
};

export default LabPanel;
